import Logo from '@assets/logo.svg?react';
import {
  useGetAssessmentFeedbackQuery,
  useUpsertAssessmentFeedbackMutation,
} from '@gql/gql-types';
import { Star } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Rating,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { AssessmentTrustPilot } from './assessment-trust-pilot';

const MIN_RATING_FOR_TRUST_PILOT = 5;

export const AssessmentFeedback = ({
  assessmentId,
  copy,
}: {
  assessmentId: string;
  copy: {
    title: string;
    ratingLabel: string;
    commentLabel: string;
  };
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showTrustPilot, setShowTrustPilot] = useState(false);
  const theme = useTheme();

  useGetAssessmentFeedbackQuery({
    variables: { assessmentId },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.assessmentFeedback) {
        setIsSubmitted(true);

        // Notify the parent window that the feedback has been submitted.
        if (window.self !== window.top) {
          window.parent.postMessage('INTERVIEW_FEEDBACK_SUBMITTED', '*');
        }
      }
      setLoading(false);
    },
  });

  const [upsertFeedback] = useUpsertAssessmentFeedbackMutation();

  const handleSubmit = async () => {
    await upsertFeedback({
      variables: { input: { assessmentId, rating, comment } },
      onCompleted: () => {
        // Show TrustPilot for high ratings (4 or 5)
        if (rating && rating >= MIN_RATING_FOR_TRUST_PILOT) {
          setShowTrustPilot(true);
        } else {
          if (window.self !== window.top) {
            setIsSubmitted(true);
            window.parent.postMessage('INTERVIEW_FEEDBACK_SUBMITTED', '*');
          }
        }
      },
      onError: (error) => {
        console.error('Error submitting feedback:', error);
        enqueueSnackbar('Error submitting feedback', { variant: 'error' });
      },
    });
  };

  const handleRatingChange = (newValue: number | null) => {
    if (!newValue) {
      return;
    }
    setRating(newValue);
    upsertFeedback({
      variables: {
        input: {
          assessmentId,
          rating: newValue,
          comment,
          isTechnicalDifficulty: false,
        },
      },
    });
  };

  const handleTrustPilotContinue = () => {
    setShowTrustPilot(false);
    // Notify the parent window that the feedback flow is complete
    if (window.self !== window.top) {
      setIsSubmitted(true);
      window.parent.postMessage('INTERVIEW_FEEDBACK_SUBMITTED', '*');
    }
  };

  if (loading) {
    return null;
  }

  if (showTrustPilot) {
    return <AssessmentTrustPilot onContinue={handleTrustPilotContinue} />;
  }

  if (isSubmitted) {
    return null;
  }

  return (
    <>
      <Logo
        style={{
          width: 70,
          height: 64,
          background: 'transparent',
          filter: theme.palette.mode === 'dark' ? 'none' : 'invert(1)',
          display: 'block',
          margin: '0 auto',
        }}
      />

      <Typography variant='h4' sx={{ fontSize: '1.75rem', mt: 3, mb: 2 }}>
        {copy.title}
      </Typography>

      <Typography variant='body1'>
        You are one step closer to being an Alignerr
      </Typography>
      <Box sx={{ mt: 6, mb: 2 }}>
        <Typography component='legend' sx={{ mb: 1, fontStyle: 'italic' }}>
          {copy.ratingLabel}
        </Typography>
        <Rating
          value={rating}
          onChange={(_, newValue) => handleRatingChange(newValue)}
          icon={<Star fontSize='large' />}
          emptyIcon={<Star fontSize='large' />}
          size='large'
        />
      </Box>

      <Collapse in={Boolean(rating)} timeout={300}>
        <Typography component='legend' sx={{ mb: 1, fontStyle: 'italic' }}>
          {copy.commentLabel}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          variant='outlined'
          sx={{ mb: 2 }}
        />

        <Button variant='contained' color='primary' onClick={handleSubmit}>
          Submit feedback
        </Button>
      </Collapse>
    </>
  );
};
